import { useState } from "react";
import { describeVoters, skipThreshold, type ListenerDoc } from "radio-shared";
import { api } from "../api";

/**
 * Le skip est collectif : on demande, on ne décide pas seul — sauf pour son
 * propre son, et sauf quand on est seul à écouter (le seuil tombe alors à 1).
 *
 * Tout est montré **avant** de cliquer : combien de voix il faut, qui a déjà
 * voté, et si on a soi-même voté. Un simple « (1/3) » ne disait pas qu'il
 * s'agissait de votes, et rien ne signalait l'arrivée d'une voix.
 */
export function SkipButton({
  disabled,
  skipVotes,
  listeners,
  myUid,
}: {
  disabled: boolean;
  skipVotes: Record<string, true> | undefined;
  listeners: ListenerDoc[];
  myUid: string;
}) {
  const [error, setError] = useState<string | null>(null);

  const needed = skipThreshold(listeners.length);
  const voters = describeVoters(skipVotes, listeners);
  const iVoted = Boolean(skipVotes?.[myUid]);
  /** Seul à l'écoute : le clic suffit, inutile d'afficher un décompte. */
  const decisive = needed <= 1;

  const vote = async () => {
    setError(null);
    try {
      await api.skip();
    } catch {
      setError("raté");
    }
  };

  return (
    <div className="skip">
      <button
        className={`btn${voters.length > 0 ? " btn--voted" : ""}`}
        type="button"
        onClick={() => void vote()}
        disabled={disabled || iVoted}
        title={
          decisive
            ? "tu es seul à écouter : ça passe tout de suite"
            : iVoted
              ? "ta voix est comptée"
              : `${needed} voix pour passer ce son`
        }
      >
        {iVoted ? "✓ voté" : "⏭ passer"}
        {!decisive && ` · ${voters.length}/${needed}`}
      </button>

      {voters.length > 0 && (
        <div className="skip__voters" title={`${voters.map((v) => v.name).join(", ")} veulent passer`}>
          {voters.slice(0, 5).map((v) =>
            v.photo ? (
              <img key={v.uid} className="avatar avatar--sm" src={v.photo} alt={v.name} />
            ) : (
              <span key={v.uid} className="avatar avatar--sm avatar--initial">
                {v.name.slice(0, 1).toUpperCase()}
              </span>
            ),
          )}
        </div>
      )}

      {error && <span className="skip__votes">{error}</span>}
    </div>
  );
}
